/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { editImage } from '../services/geminiService';
import { generateShotVariants } from '../services/nanoService';
import { Character, Dogma, ImageFile, NanoApplyPayload } from '../types';
import {
  PencilIcon,
  XMarkIcon,
  ChevronDownIcon,
  SparklesIcon,
  CameraIcon
} from './icons';
import { ImageUpload } from './PromptForm';

/**
 * Helper component for composition variants with safe ObjectURL handling
 */
const CompositionVariantCard: React.FC<{
  variant: { label: string; image: ImageFile };
  onClick: () => void;
}> = ({ variant, onClick }) => {
  const imageUrl = useMemo(() => {
    const file = variant.image?.file;
    if (!file || !(file instanceof Blob)) return null;
    try {
      return URL.createObjectURL(file);
    } catch (err) {
      console.error('[CompositionVariantCard] Failed to create ObjectURL:', err);
      return null;
    }
  }, [variant.image?.file]);

  useEffect(() => {
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  if (!imageUrl) {
    return (
      <div className="w-full aspect-video bg-gray-800 rounded-lg flex items-center justify-center">
        <span className="text-xs text-gray-500">Image indisponible</span>
      </div>
    );
  }

  return (
    <div
      className="group relative cursor-pointer border-2 border-transparent hover:border-indigo-500 rounded-lg overflow-hidden transition-all"
      onClick={onClick}
    >
      <img src={imageUrl} className="w-full aspect-video object-cover" alt={variant.label} />
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-2 pt-6">
        <span className="text-[10px] font-bold text-white block truncate">{variant.label}</span>
      </div>
    </div>
  );
};

interface AIEditorModalProps {
  image: ImageFile;
  onClose: () => void;
  onConfirm: (newImage: ImageFile) => void;
  dogma: Dogma | null;
  // === NANO BANANA PRO: Stylet alignment props ===
  onApply?: (payload: NanoApplyPayload) => void;  // For returning prompt + image + meta
  segmentIndex?: number | null;  // null=character, 0=root, 1..N=extension
  target?: 'root' | 'extension' | 'character';  // Derived from segmentIndex
  initialPrompt?: string;  // Pre-fill instruction for alignment mode
  apiKey?: string;
  characters?: Character[];
}

const AIEditorModal: React.FC<AIEditorModalProps> = ({
  image,
  onClose,
  onConfirm,
  dogma,
  onApply,
  segmentIndex = null,
  target = 'character',
  initialPrompt = '',
  characters = [],
  apiKey,
}) => {
  // PIVOT SYSTEM: We use workingImage as the base for edits.
  // Initially it's the props image, but user can "Commit" an edit to make it the new base.
  const [workingImage, setWorkingImage] = useState<ImageFile>(image);

  const [prompt, setPrompt] = useState(initialPrompt);
  const [styleImage, setStyleImage] = useState<ImageFile | null>(null);
  const [editedImage, setEditedImage] = useState<ImageFile | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cameraNotes, setCameraNotes] = useState<string>('');
  const [movementNotes, setMovementNotes] = useState<string>('');

  const [model, setModel] = useState<'nano' | 'nano-pro'>('nano-pro');
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);

  // New: View Mode for Composition Wizard
  const [viewMode, setViewMode] = useState<'edit' | 'composition'>('edit');
  const [compositionVariants, setCompositionVariants] = useState<{ label: string, image: ImageFile }[]>([]);

  // Regen State
  const [isRegenerating, setIsRegenerating] = useState(false);

  // Detect if we're in alignment mode (for stylet)
  const isAlignmentMode = !!onApply;

  // Safe ObjectURL management with cleanup
  // Memoize the display image URL to prevent memory leaks
  const displayImageUrl = useMemo(() => {
    const file = editedImage?.file ?? workingImage?.file;
    if (!file || !(file instanceof Blob)) {
      console.warn('[AIEditorModal] No valid file for ObjectURL');
      return null;
    }
    try {
      return URL.createObjectURL(file);
    } catch (err) {
      console.error('[AIEditorModal] Failed to create ObjectURL:', err);
      return null;
    }
  }, [editedImage?.file, workingImage?.file]);

  // Cleanup ObjectURL on unmount or when it changes
  useEffect(() => {
    return () => {
      if (displayImageUrl) {
        URL.revokeObjectURL(displayImageUrl);
      }
    };
  }, [displayImageUrl]);

  const handleRegenerate = async (overridePrompt?: string) => {
    const finalPromptText = overridePrompt || prompt;

    if (!finalPromptText.trim()) {
      setError('Please provide an editing instruction.');
      return;
    }
    setIsRegenerating(true); // Specific loading state for regen
    setError(null);
    // Don't clear editedImage, we want to replace it

    try {
      // PIVOT SYSTEM: Using workingImage which allows accumulated edits
      const imagesToProcess = [workingImage];
      let fullPrompt = finalPromptText;

      if (styleImage) {
        imagesToProcess.push(styleImage);
        fullPrompt = `${finalPromptText}. Apply the style from @image2 to the edit on @image1.`;
      }

      // Use correct Gemini image generation models
      const modelId = model === 'nano' ? 'gemini-2.5-flash-preview-image-generation' : 'gemini-2.5-flash-preview-image-generation';
      const result = await editImage(imagesToProcess, fullPrompt, dogma, modelId, apiKey, characters);

      setEditedImage(result);

      // Auto-derive camera/movement notes from prompt for alignment mode
      if (isAlignmentMode) {
        const cameraMatch = finalPromptText.match(/(?:camera|angle|shot|plan)[:]*\s*([^.]+)/i);
        if (cameraMatch) setCameraNotes(cameraMatch[1].trim());

        const movementMatch = finalPromptText.match(/(?:movement|motion|dolly|pan|zoom)[:]*\s*([^.]+)/i);
        if (movementMatch) setMovementNotes(movementMatch[1].trim());
      }
    } catch (err: any) {
      const isAuthError = err.message?.includes('401') || err.status === 401 || err.message?.includes('API_KEY');
      const message = isAuthError
        ? "⚠️ Échec de l'édition : Clé API manquante ou invalide.\nVeuillez configurer votre clé Gemini dans les paramètres."
        : (err instanceof Error ? err.message : 'Failed to edit image.');
      setError(message);
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleGenerateCompositions = async () => {
    setIsLoading(true);
    setError(null);
    setCompositionVariants([]);

    try {
      // Define Expert Compositions
      // We pass descriptive instructions for the AI
      const compositions = [
        "Rule of Thirds (Subject Left)",
        "Rule of Thirds (Subject Right)",
        "Symmetrical Center Composition",
        "Golden Ratio Composition"
      ];

      const result = await generateShotVariants({
        baseImage: workingImage,
        shotList: compositions,
        dogma,
        quality: 'fast', // Nano Pro is slower, Flash is good for structure
        apiKey,
        characters
      });

      if (result.variants) {
        const variants = result.variants
          .filter(v => v.previewImage)
          .map(v => ({
            label: v.label,
            image: v.previewImage!
          }));
        setCompositionVariants(variants);
      }
    } catch (err: any) {
      const isAuthError = err.message?.includes('401') || err.status === 401 || err.message?.includes('API_KEY');
      const message = isAuthError
        ? "⚠️ Échec de la composition : Clé API manquante ou invalide.\nVeuillez configurer votre clé Gemini dans les paramètres."
        : (err instanceof Error ? err.message : 'Failed to generate compositions.');
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };


  const handleCommit = () => {
    if (editedImage) {
      setWorkingImage(editedImage);
      setEditedImage(null);
      setPrompt(''); // Clear prompt for next step
      // If we are in alignment mode, we might want to update the camera notes accumulated?
      // For now, keep it simple.
    }
  };

  const handleConfirm = () => {
    const finalImage = editedImage || workingImage;
    if (finalImage) {
      onConfirm(finalImage);
      onClose(); // Close after confirming
    }
  };

  // For Nano Banana Pro Stylet / Alignment Mode
  const handleApplyPrompt = () => {
    const finalImage = editedImage || workingImage;
    if (onApply && finalImage) {
      onApply({
        previewPrompt: prompt,
        previewImage: finalImage,
        cameraNotes: cameraNotes || undefined,
        movementNotes: movementNotes || undefined,
        target: target,
        segmentIndex: segmentIndex
      });
      onClose(); // Close after applying
    }
  };


  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md p-4 animate-in fade-in duration-300">
      <div className="w-full max-w-6xl h-[85vh] bg-[#121212] rounded-2xl border border-gray-800 shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 bg-[#1a1a1a]">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-500/10 rounded-lg">
              <PencilIcon className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Nano Editor</h3>
              <p className="text-xs text-gray-400">
                {isAlignmentMode ? 'Recadrage & Retouche (Stylet)' : 'Retouche Créative'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Model Selector */}
            <div className="relative">
              <button
                onClick={() => setIsModelDropdownOpen(!isModelDropdownOpen)}
                className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-xs font-medium text-gray-300 transition-colors border border-gray-700"
              >
                {model === 'nano' ? '⚡ Nano (Rapide)' : '🍌 Nano Pro (Qualité)'}
                <ChevronDownIcon className="w-3 h-3" />
              </button>
              {isModelDropdownOpen && (
                <div className="absolute top-full right-0 mt-2 w-48 bg-[#1f1f1f] border border-gray-700 rounded-lg shadow-xl overflow-hidden z-50">
                  <button
                    onClick={() => { setModel('nano'); setIsModelDropdownOpen(false); }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                  >
                    ⚡ Nano (Rapide)
                  </button>
                  <button
                    onClick={() => { setModel('nano-pro'); setIsModelDropdownOpen(false); }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                  >
                    🍌 Nano Pro (Qualité)
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-grow flex gap-6 overflow-hidden p-6">
          {/* Image Preview Area */}
          <div className="w-2/3 relative bg-black rounded-xl border border-gray-700/30 overflow-hidden group flex flex-col">
            <div className="flex-grow relative flex items-center justify-center bg-[url('https://www.transparenttextures.com/patterns/dark-matter.png')]">
              {displayImageUrl ? (
                <img
                  src={displayImageUrl}
                  alt="Image to edit"
                  className="max-w-full max-h-full object-contain shadow-2xl"
                />
              ) : (
                <div className="flex flex-col items-center justify-center text-gray-500 gap-2">
                  <PencilIcon className="w-12 h-12 opacity-30" />
                  <span className="text-sm">Aucune image disponible</span>
                </div>
              )}

              {/* Diff Overlay Hint */}
              {editedImage && (
                <div className="absolute top-4 left-4 px-3 py-1 bg-black/60 backdrop-blur rounded-full text-xs font-medium text-indigo-300 border border-indigo-500/30">
                  Version Éditée
                </div>
              )}
            </div>

            {isRegenerating && (
              <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center backdrop-blur-sm z-20">
                <div className="w-12 h-12 border-4 border-t-transparent border-indigo-500 rounded-full animate-spin mb-4"></div>
                <p className="text-white font-semibold animate-pulse">
                  {model === 'nano' ? 'Flash Edit in progress...' : 'Banana Nano Processing...'}
                </p>
              </div>
            )}
            {isLoading && viewMode === 'composition' && (
              <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center backdrop-blur-sm z-20">
                <div className="w-12 h-12 border-4 border-t-transparent border-indigo-500 rounded-full animate-spin mb-4"></div>
                <p className="text-white font-semibold animate-pulse">
                  Generating Compositions...
                </p>
              </div>
            )}
          </div>

          {/* Right Control Column */}
          <div className="w-1/3 flex flex-col gap-4 h-full">

            {/* View Mode Tabs */}
            <div className="flex gap-1 bg-gray-900/50 p-1 rounded-lg border border-gray-800">
              <button
                onClick={() => setViewMode('edit')}
                className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all ${viewMode === 'edit' ? 'bg-gray-700 text-white shadow' : 'text-gray-500 hover:text-gray-300'}`}
              >
                Édition
              </button>
              <button
                onClick={() => setViewMode('composition')}
                className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all flex items-center justify-center gap-1.5 ${viewMode === 'composition' ? 'bg-indigo-900/50 text-indigo-300 border border-indigo-500/30 shadow' : 'text-gray-500 hover:text-gray-300'}`}
              >
                <CameraIcon className="w-3.5 h-3.5" />
                Composition
              </button>
            </div>

            <div className="flex-grow overflow-y-auto pr-2 flex flex-col gap-4">

              {/* MODE: EDIT */}
              {viewMode === 'edit' && (
                <>
                  <div>
                    <label htmlFor="ai-prompt" className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 block">
                      Instruction
                    </label>
                    <div className="bg-[#1a1a1a] border border-gray-700 rounded-xl p-3 shadow-inner focus-within:ring-2 focus-within:ring-indigo-500/50 transition-all">
                      <textarea
                        id="ai-prompt"
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        placeholder="Décrivez les changements..."
                        className="w-full h-24 bg-transparent focus:outline-none text-sm text-gray-200 placeholder-gray-600 resize-none leading-relaxed"
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="bg-gray-800/30 p-4 rounded-xl border border-gray-800">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-purple-400 mb-3 flex items-center gap-2">
                      <SparklesIcon className="w-3 h-3" />
                      Presets
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setPrompt('Remove the [object] from the scene completely, keep the background natural')}
                        className="px-2 py-2 text-xs bg-[#1f1f1f] hover:bg-gray-700 text-gray-300 rounded-lg border border-gray-700 transition-colors text-left"
                        disabled={isLoading}
                      >
                        🗑️ Supprimer objet
                      </button>
                      <button
                        type="button"
                        onClick={() => setPrompt('Add a [describe object] to the scene, blend naturally with lighting')}
                        className="px-2 py-2 text-xs bg-[#1f1f1f] hover:bg-gray-700 text-gray-300 rounded-lg border border-gray-700 transition-colors text-left"
                        disabled={isLoading}
                      >
                        ➕ Ajouter objet
                      </button>
                      <button
                        type="button"
                        onClick={() => setPrompt('Change the lighting to [day/night/golden hour], maintain subject details')}
                        className="px-2 py-2 text-xs bg-[#1f1f1f] hover:bg-gray-700 text-gray-300 rounded-lg border border-gray-700 transition-colors text-left"
                        disabled={isLoading}
                      >
                        💡 Éclairage
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setPrompt('Regenerate this image with the exact same composition and elements');
                        }}
                        className="px-2 py-2 text-xs bg-[#1f1f1f] hover:bg-gray-700 text-gray-300 rounded-lg border border-gray-700 transition-colors text-left"
                        disabled={isLoading}
                      >
                        🔁 Régénérer
                      </button>
                    </div>
                  </div>

                  <div>
                    <ImageUpload
                      label="Style de Référence"
                      image={styleImage}
                      onSelect={setStyleImage}
                      onRemove={() => setStyleImage(null)}
                      className="w-full h-20"
                    />
                  </div>
                </>
              )}

              {/* MODE: COMPOSITION */}
              {viewMode === 'composition' && (
                <div className="flex flex-col gap-4 animate-in slide-in-from-right-4 duration-300">
                  <div className="bg-indigo-900/20 border border-indigo-500/30 p-4 rounded-xl">
                    <h4 className="text-sm font-bold text-indigo-300 mb-2">Assistant de Cadrage</h4>
                    <p className="text-xs text-indigo-200/70 mb-4 leading-relaxed">
                      Générez automatiquement 4 variantes de cadrage professionnel tout en conservant votre sujet.
                    </p>
                    <button
                      onClick={handleGenerateCompositions}
                      disabled={isLoading}
                      className="w-full py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-lg shadow-lg flex items-center justify-center gap-2 transition-all"
                    >
                      {isLoading ? 'Analyse en cours...' : (
                        <>
                          <SparklesIcon className="w-3 h-3" />
                          Générer les Compositions
                        </>
                      )}
                    </button>
                  </div>

                  <div className="flex-grow bg-black/20 rounded-xl border border-gray-800 p-2 overflow-y-auto max-h-[400px]">
                    {compositionVariants.length === 0 ? (
                      <div className="h-40 flex flex-col items-center justify-center text-gray-600 gap-2">
                        <CameraIcon className="w-8 h-8 opacity-20" />
                        <span className="text-xs">Aucune variante générée</span>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-3">
                        {compositionVariants.map((v, i) => (
                          <CompositionVariantCard
                            key={i}
                            variant={v}
                            onClick={() => setEditedImage(v.image)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {error && <p className="text-xs text-red-400 text-center bg-red-900/20 p-2 rounded border border-red-800/50 mt-2">{error}</p>}

            <div className="mt-auto pt-4 border-t border-gray-800">
              {viewMode === 'edit' ? (
                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => handleRegenerate()}
                    disabled={isRegenerating || !prompt.trim()}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-all shadow-lg hover:shadow-xl disabled:bg-gray-800 disabled:text-gray-600 disabled:cursor-not-allowed border border-gray-600 hover:border-indigo-400 bg-gray-800 hover:bg-gray-700 text-indigo-300 font-bold`}
                  >
                    {isRegenerating ? (
                      <>
                        <div className="w-4 h-4 border-2 border-t-transparent border-indigo-300 rounded-full animate-spin"></div>
                        <span>Régénération...</span>
                      </>
                    ) : (
                      <>
                        <SparklesIcon className="w-4 h-4" />
                        <span>Régénérer (Aperçu)</span>
                      </>
                    )}
                  </button>

                  <button
                    onClick={() => {
                      if (isAlignmentMode) handleApplyPrompt();
                      else handleConfirm();
                    }}
                    disabled={isRegenerating || (!editedImage && !workingImage && !prompt.trim())} // Allow applying if we have something
                    className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-all shadow-lg hover:shadow-xl disabled:bg-gray-800 disabled:text-gray-600 disabled:cursor-not-allowed ${model === 'nano-pro'
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white'
                      : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                      }`}
                  >
                    <span className="font-semibold text-sm">
                      {isAlignmentMode ? 'Appliquer & Fermer' : 'Appliquer les changements'}
                    </span>
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => {
                    // In composition mode, we just Confirm the selected image
                    if (editedImage) handleConfirm();
                  }}
                  disabled={!editedImage}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-all shadow-lg hover:shadow-xl bg-green-600 hover:bg-green-500 text-white disabled:bg-gray-800 disabled:text-gray-600 disabled:cursor-not-allowed"
                >
                  <span className="font-semibold text-sm">Valider ce Cadrage</span>
                </button>
              )}

              {/* PIVOT COMMIT BUTTON (Common) */}
              {editedImage && (
                <div className="mt-3 p-3 bg-gray-800/50 rounded-xl flex items-center justify-between animate-in fade-in slide-in-from-bottom-2">
                  <div className="flex flex-col">
                    <span className="text-xs font-bold text-gray-300">Satisfait ?</span>
                    <span className="text-[9px] text-gray-500">Définir comme nouvelle base</span>
                  </div>
                  <button
                    onClick={handleCommit}
                    className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-xs font-bold rounded-lg transition-colors"
                  >
                    Valider l'étape
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIEditorModal;